import {
  type CrmChangedEvent,
  type EquipmentChangedEvent,
  type NotificationItem,
  REALTIME_EVENTS,
} from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { io } from 'socket.io-client';

/** Подписка на realtime-канал пользователя: новые уведомления сразу обновляют счётчик и список. */
export function useRealtime(enabled: boolean, onNotification?: (n: NotificationItem) => void) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    const socket = io({ path: '/socket.io', transports: ['websocket'], withCredentials: true });
    socket.on(REALTIME_EVENTS.notification, (n: NotificationItem) => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      onNotification?.(n);
    });
    // Задача изменилась у кого-то из участников — перечитываем списки, канбан и открытую карточку
    socket.on(REALTIME_EVENTS.taskChanged, () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['comments', 'task'] });
    });
    // Маршрут согласования продвинулся — перечитываем заявки, отпуска, график и «Согласования»
    socket.on(REALTIME_EVENTS.processChanged, (e: { entityType: string; entityId: string }) => {
      qc.invalidateQueries({ queryKey: ['bpm'] });
      qc.invalidateQueries({ queryKey: [e.entityType === 'vacation' ? 'vacations' : 'requests'] });
      qc.invalidateQueries({ queryKey: ['absences'] });
      qc.invalidateQueries({ queryKey: ['comments', e.entityType, e.entityId] });
    });
    // CRM: сделка, клиент, дело или новое сообщение — перечитываем всё CRM (запросы с ключом ['crm'])
    socket.on(REALTIME_EVENTS.crmChanged, (e: CrmChangedEvent) => {
      qc.invalidateQueries({ queryKey: ['crm'] });
      if (e.kind === 'deal') qc.invalidateQueries({ queryKey: ['comments', 'deal', e.id] });
    });
    // Производство, база оборудования, сервисные заявки
    socket.on(REALTIME_EVENTS.equipmentChanged, (e: EquipmentChangedEvent) => {
      qc.invalidateQueries({ queryKey: ['equipment'] });
      qc.invalidateQueries({ queryKey: ['helpdesk'] });
      qc.invalidateQueries({ queryKey: ['comments', e.kind, e.id] });
    });
    // Охрана труда: запись журнала, подтверждение, настройки
    socket.on(REALTIME_EVENTS.safetyChanged, () => {
      qc.invalidateQueries({ queryKey: ['safety'] });
    });
    // Обеды: отметка на день, гости, подписка, фиксация заказа
    socket.on(REALTIME_EVENTS.mealsChanged, () => {
      qc.invalidateQueries({ queryKey: ['meals'] });
    });
    return () => {
      socket.close();
    };
  }, [enabled, qc, onNotification]);
}
